/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Col, Row } from 'react-bootstrap';

import SpinnerButton from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateExaminedDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  getBirthDateFromPesel,
  getSexFromPesel,
  isPeselValid,
} from '../../../common/spdCore/validation/schemas';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useAuth } from '../../../common/hooks/useAuth';
import { ALL_WORKERS_WITHOUT_CONSULTATION } from '../../../common/spdCore/authorization/roles';
import { validate } from '../../../common/spdCore/contexts/ExaminedContext';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

import { ExaminedEditorRightSide } from './ExaminedEditorRightSide';
import { ExaminedEditorLeftSide } from './ExaminedEditorLeftSide';

interface ExaminedEditorProps {
  id?: string;
  examined: CreateUpdateExaminedDto;
  handleClose?: () => void;
  onCreated?: (id: string) => void;
  show?: boolean;
  readOnly: boolean;
}

export const ExaminedEditor: React.FC<ExaminedEditorProps> = props => {
  const spd = useSpdCore();
  const notifications = useNotifications();
  const [examined, setExamined] = useState(props.examined);
  // Modal visibility is managed by parent
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDrivingCategories, setSelectedDrivingCategories] = useState<
    string[]
  >(props?.examined?.drivingLicense?.split(',') || []);

  const spdCore = useSpdCore();
  const auth = useAuth();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setExamined(props.examined);
      setSelectedDrivingCategories(
        props?.examined?.drivingLicense?.split(',') || [],
      );
    }
  }, [props.show, props.examined]);

  const createExamined = async () => {
    if (selectedDrivingCategories) {
      examined.drivingLicense = selectedDrivingCategories.join(',');
    }
    const status = await validate(examined);
    if (status.valid) {
      setIsSaving(true);
      try {
        if (props.id) {
          await spd.examineds.update(props.id, examined);
        } else {
          const created = await spd.examineds.create(examined);
          if (props.onCreated && created.id) {
            props.onCreated(created.id);
          }
        }
        if (props.handleClose) {
          props.handleClose();
        }
      } catch (response) {
        setSaveErrors(await responseErrors(response as ResponseError));
        setShowSaveErrors(true);
      } finally {
        setIsSaving(false);
      }
    } else {
      setSaveErrors(status.errors);
      setShowSaveErrors(true);
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateExaminedDto>) => {
    setExamined({ ...examined, ...obj });
    if (
      Object.prototype.hasOwnProperty.call(obj, 'pesel') &&
      isPeselValid(obj.pesel) &&
      obj.pesel
    ) {
      setExamined({
        ...examined,
        dateOfBirth: getBirthDateFromPesel(obj.pesel),
        sex: getSexFromPesel(obj.pesel),
        pesel: obj.pesel,
      });
    }

    if (
      Object.prototype.hasOwnProperty.call(obj, 'postcode') &&
      obj.postcode &&
      obj.postcode.length >= 5 &&
      !examined.city
    ) {
      spdCore.examineds
        .getCityByPostCode(obj.postcode)
        .then(city => {
          if (city && city.length > 0) {
            setExamined({
              ...examined,
              city: city,
              postcode: obj.postcode,
            });
          }
        })
        .catch(error => {
          console.error('Error during take city', error);
        });
    }
  };

  const onSelectChange = (keys: string[]) => {
    setSelectedDrivingCategories(keys);
    setExamined({ ...examined, ...{ drivingLicense: keys.join(',') } });
  };

  const deleteExamined = async () => {
    if (props.id) {
      notifications.onPromise(
        spdCore.examineds.changeToInactive(props.id),
        props.handleClose,
      );
    }
  };

  const setTitle = () => {
    return props.id && !props.readOnly
      ? 'Edycja badanego'
      : props.id
        ? 'Podgląd badanego'
        : 'Dodawanie nowego badanego';
  };
  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="xl"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(event: any) => event.stopPropagation()}
      >
        <Modal.Header closeButton>
          <Modal.Title>{setTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col className="col-lg-6 col-12 mb-1">
              <ExaminedEditorLeftSide
                propertyChange={propertyChange}
                readOnly={props.readOnly}
                show={props.show}
                examined={examined}
                onSelectChange={onSelectChange}
              />
              <ValidationAlert
                show={showSaveErrors}
                errors={saveErrors}
                className="mt-3"
              />
            </Col>
            <Col className="col-lg-6 col-12 mb-1">
              <ExaminedEditorRightSide
                propertyChange={propertyChange}
                readOnly={props.readOnly}
                show={props.show}
                examined={examined}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={spdCore.examineds.getLastEditor}
            />
          )}
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          {props.id &&
            !props.readOnly &&
            auth.check(ALL_WORKERS_WITHOUT_CONSULTATION) && (
              <ConfirmationButton
                className="mx-1"
                confirmation="Czy na pewno usunąć badanego? Badania z nim powiązane pozostaną, ale badanego nie da się wybrać do nowych badań."
                variant="outline-danger"
                onOK={deleteExamined}
              >
                Usuń
              </ConfirmationButton>
            )}
          {!props.readOnly && (
            <SpinnerButton
              variant="outline-primary"
              isLoading={isSaving}
              onClick={createExamined}
            >
              Zapisz
            </SpinnerButton>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
